import { Checkbox, Chip, Link, Typography } from '@material-ui/core'
import clsx from 'clsx'
import React from 'react'

import SLAChip from '../../components/SLAChip'
import {
  assignmentLabel,
  formatWhen,
  isTerminal,
  stateTone,
  ticketStateLabel,
  Ticket
} from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import TicketPriority from './TicketPriority'
import useStyles from './styles/SupportTicketsTable.styles'

interface SupportTicketRowProps {
  ticket: Ticket
  selected: boolean
  onToggleSelected: (ticket: Ticket) => void
  selectionDisabled?: boolean
}

/**
 * One ticket as a div-grid row (§2 — div-grid is Standard at 39 screens; MUI
 * <Table> is Legacy at 14 and was what this screen used).
 */
function SupportTicketRow({
  ticket,
  selected,
  onToggleSelected,
  selectionDisabled
}: SupportTicketRowProps) {
  const classes = useStyles()

  return (
    <div
      className={clsx(classes.tableRow, {
        [classes.terminalRow]: isTerminal(ticket.state)
      })}>
      {/* Named by the ticket NUMBER, not "select row": a screen-reader user
          arriving at forty identical "select row" checkboxes cannot tell which
          one they are toggling, and the number is the reference an operator
          already uses to talk about a ticket. */}
      <div>
        <Checkbox
          size="small"
          color="primary"
          checked={selected}
          onChange={() => onToggleSelected(ticket)}
          disabled={selectionDisabled}
          inputProps={{
            'aria-label': getTranslate('Select {number}', {
              number: ticket.ticketNumber
            })
          }}
        />
      </div>
      <div>
        <TicketPriority priority={ticket.priority} />
      </div>
      <div>
        {/* The ticket number is the link, so the row is keyboard-reachable and
            open-in-new-tab works. A div row, like the <tr> before it, is not
            focusable on its own. */}
        <Link
          component={LocalizedLink}
          to={`/support/tickets/detail/${ticket.id}`}>
          <bdi dir="ltr">{ticket.ticketNumber}</bdi>
        </Link>
      </div>
      <div>
        <Typography variant="body1" className={classes.titleCell}>
          {ticket.title}
        </Typography>
      </div>
      <div>
        {/* One <bdi> PER code, not one around the list: the bidi algorithm
            reorders the whole run in RTL, so a single wrapper renders A, B, C
            as C, B, A — a different list, silently (§8.1). */}
        {ticket.affectedEntityRefs.length
          ? ticket.affectedEntityRefs
              .map((reference) => `${reference.type}:${reference.code}`)
              .map((reference) => (
                <bdi dir="ltr" key={reference}>
                  {reference}
                </bdi>
              ))
              .reduce<React.ReactNode[]>(
                (items, reference, index) =>
                  index === 0 ? [reference] : [...items, ' · ', reference],
                []
              )
          : '—'}
      </div>
      <div>
        <Chip
          size="small"
          label={ticketStateLabel(ticket.state)}
          color={stateTone(ticket.state)}
        />
        <SLAChip ticket={ticket} />
      </div>
      <div>
        {/* Bare <bdi>, deliberately not the portal's usual <bdi dir="ltr">:
            assignmentLabel returns EITHER a Latin id OR a translated Farsi
            phrase, so a forced direction is wrong for one of them. dir="auto"
            infers from the first strong character and is right for both.
            Without it, the truncation ellipsis — a bidi-neutral character —
            resolves to the paragraph direction in Farsi and lands on the
            visual LEFT, reading as though the START of the id was elided.

            No `title` here. It was a mouse-only affordance: this renders a <p>,
            which is not focusable, and `title` does not fire on touch. The full
            id lives on the ticket detail screen, where it is selectable. */}
        <Typography variant="body1" component="span">
          <bdi>{assignmentLabel(ticket)}</bdi>
        </Typography>
      </div>
      <div>
        <Typography variant="body1">{formatWhen(ticket.changedAt)}</Typography>
      </div>
      <div />
    </div>
  )
}

export default React.memo(SupportTicketRow)
